import styled, { CSSObject } from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { useShop } from 'shop/hooks'
import {
  OrderItemWithVatPrices,
  OrderItem,
  OrderItemWithLimit
} from 'shop/types/cart'
import { formatMoney, getImageURL } from 'shop/utils/common'
import { ellipsis } from '../Cart/commonStyles'

type Props = {
  item: OrderItem | OrderItemWithVatPrices | OrderItemWithLimit
  showPrice?: boolean
  showLimit?: boolean
}

const ProductInfoItem = ({
  item,
  showPrice = false,
  showLimit = false
}: Props) => {
  const { config } = useShop()
  const { domain, assetHost } = config

  const renderPrices = () => {
    const { oldPriceWithVat, newPriceWithVat } = item as OrderItemWithVatPrices
    return (
      <Container>
        <Content>
          {formatMoney(parseFloat(oldPriceWithVat))}
          <GreyText>Old Price</GreyText>
        </Content>
        <Content>
          {formatMoney(parseFloat(newPriceWithVat))}
          <GreyText>New Price</GreyText>
        </Content>
      </Container>
    )
  }

  const renderLimit = () => {
    const { quantity, limit } = item as OrderItemWithLimit
    return (
      <Container isLimit={true}>
        <Content isLimit={true}>
          {quantity}
          <GreyText>Items In Cart</GreyText>
        </Content>
        <Content isLimit={true}>
          {limit}
          <GreyText>Item Limit</GreyText>
        </Content>
      </Container>
    )
  }

  return (
    <Item>
      <ProductImage
        backgroundImage={getImageURL(domain, assetHost, item.variantId)}
      />
      <ItemInfo showLimit={showLimit} showPrice={showPrice}>
        {!showLimit && item.quantity > 1 && `${item.quantity}x `}
        {item.product.name}
      </ItemInfo>
      {showPrice && renderPrices()}
      {showLimit && renderLimit()}
    </Item>
  )
}

const Item = styled.li<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '12px 0',

    '&:first-of-type': {
      paddingTop: '0'
    },
    '&:last-of-type': {
      paddingBottom: '0'
    },
    '&:not(:last-of-type)': {
      borderBottom: '1px solid #F0F0F0'
    }
  })
)

const ItemInfo = styled.span<
  StyledHTMLElement & { showLimit?: boolean; showPrice?: boolean }
>(
  ({ showLimit = false, showPrice = false }): CSSObject => ({
    display: 'block',
    paddingRight: '10px',
    fontWeight: 500,
    flexGrow: showLimit ? 0 : 1,
    marginLeft: showLimit ? '10px' : 0,
    maxWidth: showLimit || showPrice ? '50%' : '100%',
    whiteSpace: 'nowrap',
    ...ellipsis
  })
)

const Container = styled.div<StyledHTMLElement & { isLimit?: boolean }>(
  ({ isLimit = false }): CSSObject => ({
    marginLeft: 'auto',
    alignItems: 'center',
    gap: isLimit ? '24px' : '16px',
    display: 'flex'
  })
)

const Content = styled.div<StyledHTMLElement & { isLimit?: boolean }>(
  ({ isLimit = false }): CSSObject => ({
    fontSize: '16px',
    fontWeight: 500,
    textAlign: isLimit ? 'center' : 'left'
  })
)

const GreyText = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    fontSize: '10px',
    display: 'flex',
    justifyContent: 'center',
    color: '#8C8C8C'
  })
)

const ProductImage = styled.div<
  StyledHTMLElement & { backgroundImage: string }
>(({ theme, backgroundImage }) => ({
  background: `lightgray url(${backgroundImage})`,
  alignSelf: 'center',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  marginRight: '12px',
  borderRadius: theme.borderRadius,
  flex: '0 0 30px',
  height: '30px',
  [theme['mediaQueries']['viewport6']]: {
    flex: '0 0 40px',
    height: '40px'
  },
  [theme['mediaQueries']['viewport7']]: {
    flex: '0 0 45px',
    height: '45px'
  }
}))

export default ProductInfoItem
